import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ArrowLeft, Download, Info, Lock } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, download } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMoney, localIsoDate, MONTH_NAMES } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
  useToast,
  type BadgeTone,
} from '../components/ui';

/* -------------------------------------------------------------------------- */
/* What every report shares                                                    */
/* -------------------------------------------------------------------------- */

/** A line's money: its base-currency total and what it is made of (domain/financial-reports.ts). */
export interface Figure {
  base: string;
  complete: boolean;
  unconverted: Array<{ currency: string; amount: string }>;
  atTodayRate: boolean;
  byCurrency: Array<{ currency: string; amount: string; base: string | null }>;
  count: number;
}

export type ReportStatus = 'VERIFIED' | 'PREVIEW' | 'UNVERIFIED' | 'PARTIAL' | 'NOT_RECONCILED' | 'SETUP_NEEDED' | 'UNAVAILABLE' | 'RESTRICTED';

export interface ReportContext {
  generatedAt: string;
  today: string;
  baseCurrency: string;
  from: string | null;
  to: string | null;
  asOf: string | null;
  cutover: { date: string | null; status: 'NONE' | 'DRAFT' | 'CONFIRMED'; confirmedAt: string | null };
  status: ReportStatus;
  notices: Array<{ code: string; details?: Record<string, unknown> }>;
  access: { partners: boolean; historical: boolean; payments: boolean; expenses: boolean; payroll: boolean };
}

export const STATUS_TONE: Record<ReportStatus, BadgeTone> = {
  VERIFIED: 'success',
  PREVIEW: 'info',
  UNVERIFIED: 'warning',
  PARTIAL: 'warning',
  NOT_RECONCILED: 'error',
  SETUP_NEEDED: 'warning',
  UNAVAILABLE: 'neutral',
  RESTRICTED: 'neutral',
};

/** The reports, in the order the sidebar lists them; three of them are built on partners' money. */
export const REPORTS: Array<{ to: string; label: TranslationKey; partners?: boolean; asOf?: boolean }> = [
  { to: '/financial-reports', label: 'nav.frOverview' },
  { to: '/financial-reports/profit-and-loss', label: 'nav.frPnl' },
  { to: '/financial-reports/balance-sheet', label: 'nav.frBalanceSheet', partners: true, asOf: true },
  { to: '/financial-reports/cash-flow', label: 'nav.frCashFlow', partners: true },
  { to: '/financial-reports/partners', label: 'nav.frPartners', partners: true },
  { to: '/financial-reports/monthly', label: 'nav.frMonthly' },
  { to: '/financial-reports/reconciliation', label: 'nav.frReconciliation' },
];

const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** The period presets, from this computer's calendar day; the API checks every date it is sent. */
function presetRange(preset: string, today: string): { from: string; to: string } | null {
  const [y, m] = today.split('-').map(Number);
  switch (preset) {
    case 'thisMonth':
      return { from: `${y}-${pad(m)}-01`, to: today };
    case 'lastMonth': {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { from: `${py}-${pad(pm)}-01`, to: `${py}-${pad(pm)}-${pad(lastDay(py, pm))}` };
    }
    case 'ytd':
      return { from: `${y}-01-01`, to: today };
    case 'thisYear':
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'lastYear':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default:
      return null;
  }
}

/**
 * The period and the day a report reads, kept in the address bar: moving from one
 * report to another keeps them, a drilldown can be linked to, and the browser's
 * back button returns to what was on screen.
 */
export function useReportParams() {
  const [params, setParams] = useSearchParams();
  const today = localIsoDate();
  const preset = params.get('preset') ?? 'thisMonth';
  const range = presetRange(preset, today);
  const from = params.get('from') ?? range?.from ?? `${today.slice(0, 7)}-01`;
  const to = params.get('to') ?? range?.to ?? today;
  const asOf = params.get('asOf') ?? today;
  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };
  return {
    preset,
    from,
    to,
    asOf,
    today,
    search: params.toString(),
    setPreset: (value: string) => {
      const r = presetRange(value, today);
      update(r ? { preset: value, from: null, to: null } : { preset: 'custom', from, to });
    },
    setFrom: (value: string) => update({ preset: 'custom', from: value, to }),
    setTo: (value: string) => update({ preset: 'custom', from, to: value }),
    setAsOf: (value: string) => update({ asOf: value }),
  };
}

export function useReport<T>(path: string, params: Record<string, string | undefined>, enabled = true) {
  return useQuery({
    queryKey: ['financial-reports', path, params],
    queryFn: () => api.get<T>(path, params),
    enabled,
    // Never stale on screen for long: partner debt is worked out at the moment it is asked.
    staleTime: 0,
  });
}

/** The money of a figure in the base currency, a loss in red with its sign, and what it leaves out. */
export function FigureValue({
  figure,
  currency,
  signed = false,
  className,
}: {
  figure: Figure | null | undefined;
  currency: string;
  /** Show a loss as a loss: red, with its minus sign. */
  signed?: boolean;
  className?: string;
}) {
  const { t, locale } = useI18n();
  if (!figure) return <span className="text-muted">—</span>;
  const negative = Number(figure.base) < 0;
  return (
    <span className={clsx('inline-flex items-center gap-1 tabular', className)}>
      <span className={clsx(signed && negative && 'text-error-fg dark:text-error-dark')}>{formatMoney(figure.base, currency, locale)}</span>
      {figure.atTodayRate && (
        <span className="text-caption text-muted" title={t('fr.atTodayRate')} aria-label={t('fr.atTodayRate')}>
          ≈
        </span>
      )}
      {!figure.complete && (
        <span
          title={t('fr.leftOut', { amounts: figure.unconverted.map((u) => formatMoney(u.amount, u.currency, locale)).join(', ') })}
          aria-label={t('fr.leftOut', { amounts: figure.unconverted.map((u) => formatMoney(u.amount, u.currency, locale)).join(', ') })}
        >
          <AlertTriangle className="h-3.5 w-3.5 text-warning-fg dark:text-warning-dark" aria-hidden />
        </span>
      )}
    </span>
  );
}

/** A loss reads as one: the word beside the number, not only its colour. */
export function ProfitWord({ value }: { value: string | number }) {
  const { t } = useI18n();
  const n = Number(value);
  if (n === 0) return null;
  return <Badge tone={n < 0 ? 'error' : 'success'}>{n < 0 ? t('fr.loss') : t('fr.profit')}</Badge>;
}

function noticeVars(details: Record<string, unknown> | undefined, locale: 'en' | 'ar'): Record<string, string | number> {
  const vars: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(details ?? {})) {
    if (Array.isArray(value)) vars[key] = value.map((v) => (typeof v === 'object' && v ? JSON.stringify(v) : String(v).slice(0, 7))).join(', ');
    else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) vars[key] = formatDate(value, locale);
    else if (typeof value === 'string' || typeof value === 'number') vars[key] = value;
  }
  return vars;
}

/** What the report says about itself: how far to trust it, in what currency, on what basis, as of when. */
export function ContextBar({ context, hideNotices = false }: { context: ReportContext; hideNotices?: boolean }) {
  const { t, locale } = useI18n();
  const cutover =
    context.cutover.status === 'NONE' || !context.cutover.date
      ? t('fr.cutoverNone')
      : t(context.cutover.status === 'CONFIRMED' ? 'fr.cutoverOn' : 'fr.cutoverDraft', { date: formatDate(context.cutover.date, locale) });
  const notices = hideNotices ? [] : context.notices;
  return (
    <div className="mb-5 space-y-2">
      <div className="surface flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg px-4 py-3 text-caption text-muted">
        <span className="inline-flex items-center gap-2">
          <Badge tone={STATUS_TONE[context.status]} dot>
            {t(`fr.status.${context.status}` as TranslationKey)}
          </Badge>
          <span>{t(`fr.statusHint.${context.status}` as TranslationKey)}</span>
        </span>
        <span>{t('fr.inBase', { currency: context.baseCurrency })}</span>
        <span>{cutover}</span>
        <span className="hidden md:inline">{t('fr.basis')}</span>
        <span>{t('fr.generated', { time: formatDateTime(context.generatedAt, locale) })}</span>
        {context.status === 'SETUP_NEEDED' && (
          <Link to="/financial-setup" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
            {t('fr.setupLink')}
          </Link>
        )}
      </div>
      {notices.length > 0 && (
        <ul className="space-y-1.5">
          {notices.map((n) => (
            <li key={n.code} className="flex items-start gap-2 text-body-sm text-[var(--text-secondary)]">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
              <span>{t(`fr.notice.${n.code}` as TranslationKey, noticeVars(n.details, locale))}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Downloads a report as the screen shows it — the same answer, the same permissions, in the reader's language. */
export function ExportButton({ path, params }: { path: string; params: Record<string, string | undefined> }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      loading={busy}
      icon={<Download className="h-4 w-4" aria-hidden />}
      onClick={async () => {
        setBusy(true);
        try {
          await download(path, { ...params, locale });
        } catch (error) {
          toast.error(error instanceof Error ? error.message : String(error));
        } finally {
          setBusy(false);
        }
      }}
    >
      {t('fr.export')}
    </Button>
  );
}

/** The reports' own tabs, keeping the period as you move between them. */
export function ReportTabs() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { search } = useLocation();
  return (
    <nav className="mb-4 flex gap-1 overflow-x-auto overflow-y-hidden border-b hairline" aria-label={t('nav.groupReports')}>
      {REPORTS.filter((r) => !r.partners || can(P.PARTNERS_VIEW)).map((r) => (
        <NavLink
          key={r.to}
          to={`${r.to}${search}`}
          end
          className={({ isActive }) =>
            clsx(
              'relative whitespace-nowrap px-3.5 py-2.5 text-body-sm font-medium transition-colors duration-fast',
              isActive ? 'text-primary-600 dark:text-primary-400' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )
          }
        >
          {({ isActive }) => (
            <>
              {t(r.label)}
              {isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-pill bg-primary" aria-hidden />}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** The period of revenue and costs: a preset, or two days. */
export function PeriodControls() {
  const { t } = useI18n();
  const p = useReportParams();
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={t('fr.period')}>
        <Select value={p.preset} onChange={(e) => p.setPreset(e.target.value)} className="!w-auto">
          {['thisMonth', 'lastMonth', 'ytd', 'thisYear', 'lastYear', 'custom'].map((preset) => (
            <option key={preset} value={preset}>
              {t(`fr.period.${preset}` as TranslationKey)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('fr.from')}>
        <Input type="date" value={p.from} max={p.to} onChange={(e) => e.target.value && p.setFrom(e.target.value)} className="!w-auto" />
      </Field>
      <Field label={t('fr.to')}>
        <Input type="date" value={p.to} min={p.from} onChange={(e) => e.target.value && p.setTo(e.target.value)} className="!w-auto" />
      </Field>
    </div>
  );
}

/** The day balances are read on — never one still to come. */
export function AsOfControl() {
  const { t } = useI18n();
  const p = useReportParams();
  return (
    <Field label={t('fr.asOf')}>
      <Input type="date" value={p.asOf} max={p.today} onChange={(e) => e.target.value && p.setAsOf(e.target.value)} className="!w-auto" />
    </Field>
  );
}

/** A report's page: its title, the tabs, its controls and export, then itself. */
export function ReportPage({
  title,
  subtitle,
  controls,
  exportPath,
  exportParams,
  children,
}: {
  title: string;
  subtitle?: string;
  controls: ReactNode;
  exportPath?: string;
  exportParams?: Record<string, string | undefined>;
  children: ReactNode;
}) {
  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />
      <ReportTabs />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        {controls}
        {exportPath && <ExportButton path={exportPath} params={exportParams ?? {}} />}
      </div>
      {children}
    </>
  );
}

/** Where a figure's rows are, with the period it was read for. */
export function drillHref(line: string, params: { from: string; to: string; categoryId?: string | null; currency?: string }) {
  const q = new URLSearchParams({ line, from: params.from, to: params.to });
  if (params.categoryId) q.set('categoryId', params.categoryId);
  if (params.currency) q.set('currency', params.currency);
  return `/financial-reports/drilldown?${q.toString()}`;
}

export function monthLabel(month: string, locale: 'en' | 'ar', short = false): string {
  const [y, m] = month.split('-').map(Number);
  const name = MONTH_NAMES[locale][m - 1];
  return short ? `${name.slice(0, locale === 'ar' ? undefined : 3)} ${String(y).slice(2)}` : `${name} ${y}`;
}

export const CHART = {
  revenue: 'var(--vg-chart-2)',
  expenses: 'var(--vg-chart-1)',
  profit: 'var(--vg-chart-5)',
  loss: 'var(--vg-chart-8)',
  series: ['var(--vg-chart-1)', 'var(--vg-chart-4)', 'var(--vg-chart-3)', 'var(--vg-chart-7)'],
  grid: 'var(--vg-chart-grid)',
};

export const tooltipStyle = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  color: 'var(--text-primary)',
  fontSize: 13,
};

/** A balance on a day: per currency, or why there is none. */
function StockCard({ label, stock, onOpen }: { label: string; stock: { status: ReportStatus; asOf: string; byCurrency?: Array<{ currency: string; amount: string }> }; onOpen?: () => void }) {
  const { t, locale } = useI18n();
  const shown = stock.byCurrency?.filter((c) => Number(c.amount) !== 0) ?? [];
  const Wrapper = onOpen && stock.byCurrency ? 'button' : 'div';
  return (
    <Wrapper onClick={onOpen} className={clsx('surface w-full rounded-lg p-4 text-start', onOpen && stock.byCurrency && 'cursor-pointer transition-colors duration-fast hover:border-primary/50')}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{label}</span>
        <Badge tone={STATUS_TONE[stock.status]}>{t(`fr.status.${stock.status}` as TranslationKey)}</Badge>
      </div>
      {stock.byCurrency ? (
        <div className="mt-2 space-y-0.5">
          {shown.length === 0 ? (
            <div className="text-metric tabular text-[var(--text-primary)]">{t('fr.card.none')}</div>
          ) : (
            shown.map((c, i) => (
              <div key={c.currency} className={clsx('tabular text-[var(--text-primary)]', i === 0 ? 'text-metric' : 'text-title')}>
                {formatMoney(c.amount, c.currency, locale)}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2 text-body-sm text-muted">
          {stock.status === 'RESTRICTED' && <Lock className="h-4 w-4" aria-hidden />}
          {t(`fr.statusHint.${stock.status}` as TranslationKey)}
        </div>
      )}
      <div className="mt-1 text-caption text-muted">{t('fr.card.asOf', { date: formatDate(stock.asOf, locale) })}</div>
    </Wrapper>
  );
}

function FigureCard({ label, figure, currency, signed, period, onOpen }: { label: string; figure: Figure; currency: string; signed?: boolean; period: string; onOpen?: () => void }) {
  const Wrapper = onOpen ? 'button' : 'div';
  return (
    <Wrapper onClick={onOpen} className={clsx('surface w-full rounded-lg p-4 text-start', onOpen && 'cursor-pointer transition-colors duration-fast hover:border-primary/50')}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{label}</span>
        {signed && <ProfitWord value={figure.base} />}
      </div>
      <div className="mt-2 text-metric">
        <FigureValue figure={figure} currency={currency} signed={signed} />
      </div>
      <div className="mt-1 text-caption text-muted">{period}</div>
    </Wrapper>
  );
}

/* -------------------------------------------------------------------------- */
/* Financial overview (§5)                                                     */
/* -------------------------------------------------------------------------- */

export function FinancialOverviewPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { from, to, search } = useReportParams();
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/overview', { from, to });

  const period = t('fr.card.period', { from: formatDate(from, locale), to: formatDate(to, locale) });
  const base: string = data?.context?.baseCurrency ?? 'EGP';
  const months = useMemo(
    () =>
      (data?.months ?? []).map((m: any) => ({
        ...m,
        label: monthLabel(m.month, locale, true),
        revenue: Number(m.revenue),
        expenses: Number(m.expenses),
        profit: Number(m.profit),
      })),
    [data, locale],
  );
  const cashMonths = useMemo(() => {
    const rows = (data?.cashByMonth ?? []).filter((r: any) => r.currency === base);
    return rows.map((r: any) => ({ label: monthLabel(r.month, locale, true), inflow: Number(r.inflow), outflow: Number(r.outflow) }));
  }, [data, base, locale]);
  const otherCurrencyCash = (data?.cashByMonth ?? []).some((r: any) => r.currency !== base);

  const goPnl = () => navigate(`/financial-reports/profit-and-loss?${search}`);
  const toMoney = (v: unknown) => formatMoney(Number(v), base, locale);

  return (
    <ReportPage title={t('nav.frOverview')} controls={<PeriodControls />} exportPath="/financial-reports/overview/export" exportParams={{ from, to }}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-5">
          <ContextBar context={data.context} />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <FigureCard label={t('fr.card.revenue')} figure={data.cards.revenue} currency={base} period={period} onOpen={() => navigate(drillHref('CUSTOMER_PAYMENTS', { from, to }))} />
            <FigureCard label={t('fr.card.salaries')} figure={data.cards.salaries} currency={base} period={period} onOpen={goPnl} />
            <FigureCard label={t('fr.card.otherExpenses')} figure={data.cards.otherExpenses} currency={base} period={period} onOpen={goPnl} />
            <FigureCard label={t('fr.card.operatingExpenses')} figure={data.cards.operatingExpenses} currency={base} period={period} onOpen={goPnl} />
            <FigureCard label={t('fr.card.netProfit')} figure={data.cards.netProfit} currency={base} period={period} signed onOpen={goPnl} />
            <StockCard label={t('fr.card.cash')} stock={data.cards.cash} onOpen={() => navigate(`/financial-reports/cash-flow?${search}`)} />
            <StockCard label={t('fr.card.partnerLoans')} stock={data.cards.partnerLoans} onOpen={() => navigate(`/financial-reports/partners?${search}`)} />
            <StockCard label={t('fr.card.partnerAdvances')} stock={data.cards.partnerAdvances} onOpen={() => navigate(`/financial-reports/partners?${search}`)} />
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <Card>
              <CardHeader title={t('fr.chart.revenueVsExpenses')} subtitle={t('fr.inBase', { currency: base })} />
              {months.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="h-64" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={months}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="label" stroke="var(--text-tertiary)" fontSize={12} />
                      <YAxis stroke="var(--text-tertiary)" fontSize={12} width={72} tickFormatter={(v) => Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { notation: 'compact' }).format(v)} />
                      <Tooltip formatter={toMoney} contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span className="text-[var(--text-secondary)]">{v}</span>} />
                      <Bar name={t('fr.series.revenue')} dataKey="revenue" fill={CHART.revenue} radius={[4, 4, 0, 0]} />
                      <Bar name={t('fr.series.expenses')} dataKey="expenses" fill={CHART.expenses} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title={t('fr.chart.profitTrend')} subtitle={t('fr.inBase', { currency: base })} />
              {months.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="h-64" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={months}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="label" stroke="var(--text-tertiary)" fontSize={12} />
                      <YAxis stroke="var(--text-tertiary)" fontSize={12} width={72} tickFormatter={(v) => Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { notation: 'compact' }).format(v)} />
                      <ReferenceLine y={0} stroke="var(--text-tertiary)" />
                      <Tooltip
                        formatter={(v: any, _n: any, item: any) => [
                          `${toMoney(v)}${item?.payload?.coverage?.profit !== 'COMPLETE' ? ` · ${t(`fr.coverage.profit.${item.payload.coverage.profit}` as TranslationKey)}` : ''}`,
                          t('fr.series.profit'),
                        ]}
                        contentStyle={tooltipStyle}
                      />
                      <Bar name={t('fr.series.profit')} dataKey="profit" radius={[4, 4, 4, 4]}>
                        {months.map((m: any) => (
                          <Cell key={m.month} fill={m.profit < 0 ? CHART.loss : CHART.profit} fillOpacity={m.coverage.profit === 'COMPLETE' ? 1 : 0.45} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title={t('fr.chart.breakdown')} subtitle={t('fr.inBase', { currency: base })} />
              <BreakdownBars
                rows={[
                  { label: t('fr.series.salariesCurrent'), figure: data.breakdown.salariesCurrent },
                  { label: t('fr.series.salariesHistorical'), figure: data.breakdown.salariesHistorical },
                  { label: t('fr.series.otherCurrent'), figure: data.breakdown.otherCurrent },
                  { label: t('fr.series.otherHistorical'), figure: data.breakdown.otherHistorical },
                ]}
                currency={base}
              />
            </Card>

            <Card>
              <CardHeader title={t('fr.chart.cashMonthly')} subtitle={t('fr.inBase', { currency: base })} />
              {!data.context.access.partners ? (
                <EmptyState icon={<Lock className="h-6 w-6" />} title={t('fr.status.RESTRICTED')} hint={t('fr.statusHint.RESTRICTED')} />
              ) : cashMonths.length === 0 ? (
                <EmptyState title={t('fr.noRecords')} hint={t('fr.cashChartNone')} />
              ) : (
                <div className="h-64" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cashMonths}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="label" stroke="var(--text-tertiary)" fontSize={12} />
                      <YAxis stroke="var(--text-tertiary)" fontSize={12} width={72} tickFormatter={(v) => Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { notation: 'compact' }).format(v)} />
                      <Tooltip formatter={toMoney} contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span className="text-[var(--text-secondary)]">{v}</span>} />
                      <Bar name={t('fr.series.inflow')} dataKey="inflow" fill={CHART.profit} radius={[4, 4, 0, 0]} />
                      <Bar name={t('fr.series.outflow')} dataKey="outflow" fill={CHART.expenses} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {otherCurrencyCash && <p className="mt-2 text-caption text-muted">{t('fr.notice.PER_CURRENCY')}</p>}
            </Card>
          </div>

          {data.cards.partnerLoans.byCurrency && (
            <Card>
              <CardHeader title={t('fr.chart.partners')} subtitle={t('fr.notice.NO_OFFSET')} />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('fr.currency')}</Th>
                    <Th align="end">{t('fr.series.loans')}</Th>
                    <Th align="end">{t('fr.series.advances')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.cards.partnerLoans.byCurrency.map((c: any) => (
                    <Tr key={c.currency}>
                      <Td className="font-mono text-mono-sm">{c.currency}</Td>
                      <Td align="end" className="tabular">{formatMoney(c.amount, c.currency, locale)}</Td>
                      <Td align="end" className="tabular">
                        {formatMoney(data.cards.partnerAdvances.byCurrency.find((a: any) => a.currency === c.currency)?.amount ?? 0, c.currency, locale)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          <Card>
            <CardHeader title={t('fr.coverageSummary')} action={<Link to={`/financial-reports/monthly?${search}`} className="text-body-sm text-primary-600 hover:underline dark:text-primary-400">{t('nav.frMonthly')}</Link>} />
            {data.coverage.notProvided + data.coverage.overlapChanged + data.coverage.missingDocuments + data.coverage.drafts === 0 ? (
              <p className="text-body-sm text-muted">{t('fr.coverageSummary.allClear')}</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {data.coverage.overlapChanged > 0 && <li><Badge tone="error">{t('fr.coverageSummary.overlapChanged', { n: data.coverage.overlapChanged })}</Badge></li>}
                {data.coverage.notProvided > 0 && <li><Badge tone="warning">{t('fr.coverageSummary.notProvided', { n: data.coverage.notProvided })}</Badge></li>}
                {data.coverage.missingDocuments > 0 && <li><Badge tone="warning">{t('fr.coverageSummary.missingDocuments', { n: data.coverage.missingDocuments })}</Badge></li>}
                {data.coverage.drafts > 0 && <li><Badge tone="neutral">{t('fr.coverageSummary.drafts', { n: data.coverage.drafts })}</Badge></li>}
              </ul>
            )}
          </Card>
          <p className="text-caption text-muted">{t('fr.readOnly')}</p>
        </div>
      )}
    </ReportPage>
  );
}

/** A few named parts of a total, as bars — never a pie of what may not be complete. */
function BreakdownBars({ rows, currency }: { rows: Array<{ label: string; figure: Figure }>; currency: string }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(Number(r.figure.base))));
  return (
    <ul className="space-y-3">
      {rows.map((r, i) => (
        <li key={r.label}>
          <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2 text-body-sm">
            <span className="text-[var(--text-secondary)]">{r.label}</span>
            <FigureValue figure={r.figure} currency={currency} />
          </div>
          <div className="h-2 w-full overflow-hidden rounded-pill bg-[var(--surface-sunken)]">
            <div className="h-full rounded-pill" style={{ width: `${(Math.abs(Number(r.figure.base)) / max) * 100}%`, background: CHART.series[i % CHART.series.length] }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/* Monthly summary (§10)                                                       */
/* -------------------------------------------------------------------------- */

const COVERAGE_TONE: Record<string, BadgeTone> = {
  LIVE: 'success',
  RECORDED: 'success',
  HISTORICAL: 'info',
  HISTORICAL_AND_LIVE: 'info',
  OVERLAP_CHANGED: 'error',
  NO_RECORDS: 'neutral',
  NOT_PROVIDED: 'warning',
};

export function CoverageBadge({ state }: { state: string }) {
  const { t } = useI18n();
  return <Badge tone={COVERAGE_TONE[state] ?? 'neutral'}>{t(`fr.coverage.${state}` as TranslationKey)}</Badge>;
}

export function MonthlySummaryPage() {
  const { t, locale } = useI18n();
  const { from, to } = useReportParams();
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/monthly', { from, to });
  const base: string = data?.context?.baseCurrency ?? 'EGP';

  /** A historical cell opens its record and its files; a current one, the rows behind it. */
  const cell = (figure: Figure, href: string | null) => {
    const value = <FigureValue figure={figure} currency={base} />;
    if (!href || Number(figure.base) === 0) return value;
    return (
      <Link to={href} className="hover:underline">
        {value}
      </Link>
    );
  };
  const monthEnd = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    const last = `${month.slice(0, 8)}${pad(lastDay(y, m))}`;
    return { from: month < from ? from : month, to: last > to ? to : last };
  };

  return (
    <ReportPage
      title={t('nav.frMonthly')}
      subtitle={t('fr.monthly.subtitle')}
      controls={<PeriodControls />}
      exportPath="/financial-reports/monthly/export"
      exportParams={{ from, to }}
    >
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-5">
          <ContextBar context={data.context} />
          <Card padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>{t('fr.month')}</Th>
                  <Th align="end">{t('fr.monthly.revenue')}</Th>
                  <Th align="end">{t('fr.monthly.histSalaries')}</Th>
                  <Th align="end">{t('fr.monthly.curSalaries')}</Th>
                  <Th align="end">{t('fr.monthly.histOther')}</Th>
                  <Th align="end">{t('fr.monthly.curOther')}</Th>
                  <Th align="end">{t('fr.monthly.totalExpenses')}</Th>
                  <Th align="end">{t('fr.monthly.profit')}</Th>
                  <Th align="end">{t('fr.monthly.files')}</Th>
                  <Th>{t('fr.monthly.coverage')}</Th>
                </tr>
              </thead>
              <tbody>
                {(data.years as any[]).map((year) => (
                  <MonthlyYear key={year.year} year={year} rows={data.rows.filter((r: any) => r.month.startsWith(year.year))} base={base} cell={cell} monthEnd={monthEnd} />
                ))}
                <Tr className="bg-[var(--surface-sunken)]">
                  <Td className="font-semibold">{t('fr.total')}</Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.revenue} currency={base} /></Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.historicalSalaries} currency={base} /></Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.currentSalaries} currency={base} /></Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.historicalOther} currency={base} /></Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.currentOther} currency={base} /></Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.totalExpenses} currency={base} /></Td>
                  <Td align="end" className="font-semibold"><FigureValue figure={data.total.profit} currency={base} signed /></Td>
                  <Td />
                  <Td />
                </Tr>
              </tbody>
            </Table>
          </Card>

          {data.overlaps.length > 0 && (
            <Card>
              <CardHeader title={t('fr.monthly.overlaps')} />
              <ul className="space-y-2 text-body-sm">
                {data.overlaps.map((o: any) => (
                  <li key={o.recordId}>
                    <Link to={`/historical/${o.recordId}`} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
                      {monthLabel(o.month, locale)} · {o.currency}
                    </Link>
                    <ul className="ms-4 mt-1 list-disc text-muted">
                      {o.changes.map((c: any, i: number) => (
                        <li key={i}>
                          {t('fr.monthly.overlapLine', {
                            kind: t(`fr.monthly.kind.${c.kind}` as TranslationKey),
                            currency: c.currency ?? '',
                            reviewed: c.reviewed === null ? '—' : formatMoney(c.reviewed, c.currency ?? base, locale),
                            now: c.now === null ? '—' : formatMoney(c.now, c.currency ?? base, locale),
                          })}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card>
            <CardHeader title={t('fr.monthly.sources')} />
            <ul className="space-y-1.5 text-body-sm text-[var(--text-secondary)]">
              <li className="flex gap-2"><Badge tone="success">{t('fr.coverage.LIVE')}</Badge>{t('fr.monthly.sourceLive')}</li>
              <li className="flex gap-2"><Badge tone="info">{t('fr.coverage.HISTORICAL')}</Badge>{t('fr.monthly.sourceHistorical')}</li>
              <li className="flex gap-2"><Badge tone="info">{t('fr.coverage.HISTORICAL_AND_LIVE')}</Badge>{t('fr.monthly.sourceOverlap')}</li>
              <li className="flex gap-2"><Badge tone="warning">{t('fr.coverage.NOT_PROVIDED')}</Badge>{t('fr.notice.MONTHS_NOT_PROVIDED')}</li>
            </ul>
          </Card>
        </div>
      )}
    </ReportPage>
  );
}

function MonthlyYear({
  year,
  rows,
  base,
  cell,
  monthEnd,
}: {
  year: any;
  rows: any[];
  base: string;
  cell: (figure: Figure, href: string | null) => ReactNode;
  monthEnd: (month: string) => { from: string; to: string };
}) {
  const { t, locale } = useI18n();
  return (
    <>
      {rows.map((r) => {
        const c = r.coverage;
        const span = monthEnd(r.month);
        const historical = (ids: string[]) => (ids.length === 1 ? `/historical/${ids[0]}` : ids.length > 1 ? `/historical?from=${r.month.slice(0, 7)}&to=${r.month.slice(0, 7)}` : null);
        return (
          <Tr key={r.month}>
            <Td className="whitespace-nowrap">
              <span className="text-[var(--text-primary)]">{monthLabel(r.month, locale)}</span>
              {c.standing === 'BEFORE_CUTOVER' && <span className="ms-2 text-caption text-muted">{t('fr.coverage.beforeCutover')}</span>}
              {c.partialMonth && <span className="ms-2 text-caption text-muted">{t('fr.coverage.partialMonth')}</span>}
            </Td>
            <Td align="end">{c.revenue === 'NOT_PROVIDED' ? <span className="whitespace-nowrap text-caption text-warning-fg dark:text-warning-dark">{t('fr.notProvided')}</span> : cell(r.revenue, drillHref('CUSTOMER_PAYMENTS', span))}</Td>
            <Td align="end">{c.historical.salaries.length ? cell(r.historicalSalaries, historical(c.historical.salaries)) : <span className="text-muted">—</span>}</Td>
            <Td align="end">{cell(r.currentSalaries, drillHref('SALARY_EXPENSES', span))}</Td>
            <Td align="end">{c.historical.expenses.length ? cell(r.historicalOther, historical(c.historical.expenses)) : <span className="text-muted">—</span>}</Td>
            <Td align="end">{cell(r.currentOther, drillHref('OTHER_EXPENSES', span))}</Td>
            <Td align="end"><FigureValue figure={r.totalExpenses} currency={base} /></Td>
            <Td align="end">
              {/* A month that is not complete is not a verified profit or loss: its figure is qualified, not coloured. */}
              <FigureValue figure={r.profit} currency={base} signed={c.profit === 'COMPLETE'} className={clsx(c.profit !== 'COMPLETE' && 'text-muted')} />
              {c.profit !== 'COMPLETE' && (
                <span className={clsx('block whitespace-nowrap text-caption', c.profit === 'NOT_RECONCILED' ? 'text-error-fg dark:text-error-dark' : 'text-muted')}>
                  {t(`fr.coverage.profit.${c.profit}` as TranslationKey)}
                </span>
              )}
            </Td>
            <Td align="end" className="tabular text-muted">
              {c.attachments > 0 ? c.attachments : '—'}
              {c.missingDocuments > 0 && <span className="ms-1 text-caption text-warning-fg dark:text-warning-dark">{t('fr.coverage.noFile')}</span>}
            </Td>
            <Td>
              {/* `relative` keeps the screen-reader labels inside the table's scroller: positioned from
                  the page instead, right to left they stretched the whole page sideways. */}
              <div className="relative flex min-w-[15rem] flex-wrap gap-1">
                <span className="sr-only">{t('fr.coverage.salaries')}</span>
                <CoverageBadge state={c.salaries} />
                <span className="sr-only">{t('fr.coverage.other')}</span>
                <CoverageBadge state={c.otherExpenses} />
                {c.drafts > 0 && <Badge tone="neutral">{t('fr.coverage.drafts', { n: c.drafts })}</Badge>}
              </div>
            </Td>
          </Tr>
        );
      })}
      <Tr className="bg-[var(--surface-sunken)]">
        <Td className="font-medium">{t('fr.monthly.year', { year: year.year })}</Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.revenue} currency={base} /></Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.historicalSalaries} currency={base} /></Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.currentSalaries} currency={base} /></Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.historicalOther} currency={base} /></Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.currentOther} currency={base} /></Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.totalExpenses} currency={base} /></Td>
        <Td align="end" className="font-medium"><FigureValue figure={year.profit} currency={base} signed /></Td>
        <Td />
        <Td />
      </Tr>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Reconciliation (§13)                                                        */
/* -------------------------------------------------------------------------- */

const CHECK_TONE: Record<string, BadgeTone> = { PASS: 'success', WARN: 'warning', FAIL: 'error', UNAVAILABLE: 'neutral', RESTRICTED: 'neutral' };

export function ReconciliationPage() {
  const { t } = useI18n();
  const { from, to, asOf } = useReportParams();
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/reconciliation', { from, to, asOf });

  return (
    <ReportPage
      title={t('nav.frReconciliation')}
      subtitle={t('fr.rec.subtitle')}
      controls={
        <div className="flex flex-wrap items-end gap-2">
          <AsOfControl />
          <PeriodControls />
        </div>
      }
      exportPath="/financial-reports/reconciliation/export"
      exportParams={{ from, to, asOf }}
    >
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-4">
          <ContextBar context={data.context} />
          <div className="flex items-center gap-2 text-body-sm">
            <span className="text-muted">{t('fr.rec.overall')}</span>
            <Badge tone={CHECK_TONE[data.overall]} dot>
              {t(`fr.rec.status.${data.overall}` as TranslationKey)}
            </Badge>
          </div>
          <ol className="space-y-3">
            {data.checks.map((check: any, i: number) => (
              <li key={check.code}>
                <Card>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <h3 className="text-title text-[var(--text-primary)]">
                      <span className="me-2 tabular text-muted">{i + 1}.</span>
                      {t(`fr.rec.${check.code}` as TranslationKey)}
                    </h3>
                    <Badge tone={CHECK_TONE[check.status]} dot>
                      {t(`fr.rec.status.${check.status}` as TranslationKey)}
                    </Badge>
                  </div>
                  <CheckDetails check={check} />
                </Card>
              </li>
            ))}
          </ol>
        </div>
      )}
    </ReportPage>
  );
}

function CheckDetails({ check }: { check: any }) {
  const { t, locale } = useI18n();
  const d = check.details ?? {};
  const lines: ReactNode[] = [];
  const months = (list: any[]) => list.map((x) => monthLabel(x.month, locale)).join(', ');
  switch (check.code) {
    case 'CASH_ROLLFORWARD':
    case 'BALANCE_SHEET_EQUATION':
      for (const c of d.currencies ?? []) {
        lines.push(
          <span key={c.currency} className="tabular">
            {check.code === 'CASH_ROLLFORWARD'
              ? `${formatMoney(c.opening, c.currency, locale)} + ${formatMoney(c.movements, c.currency, locale)} = ${formatMoney(c.ending, c.currency, locale)}`
              : `${formatMoney(c.assets, c.currency, locale)} = ${formatMoney(c.liabilities, c.currency, locale)} + ${formatMoney(c.equity, c.currency, locale)}`}
            {Number(c.difference) !== 0 && <span className="ms-2 text-error-fg dark:text-error-dark">{t('fr.bs.difference', { amount: formatMoney(c.difference, c.currency, locale) })}</span>}
          </span>,
        );
      }
      break;
    case 'PARTNER_LOANS_ROLLFORWARD':
    case 'PARTNER_ADVANCES_ROLLFORWARD':
      if (d.reason === 'NO_OPENING_POSITION') lines.push(t('fr.rec.noOpening'));
      for (const p of d.partners ?? []) {
        lines.push(
          <span key={`${p.partnerId}-${p.currency}`} className={clsx('tabular', !p.reconciles && 'text-error-fg dark:text-error-dark')}>
            {t('fr.rec.partnerLine', {
              name: p.name,
              currency: p.currency,
              opening: formatMoney(p.opening, p.currency, locale),
              closing: formatMoney(p.closing, p.currency, locale),
            })}
          </span>,
        );
      }
      break;
    case 'HISTORICAL_ONCE':
      for (const o of d.overlapChanged ?? []) {
        lines.push(
          <Link key={o.recordId} to={`/historical/${o.recordId}`} className="text-primary-600 hover:underline dark:text-primary-400">
            {monthLabel(o.month, locale)} · {o.currency} — {t('fr.coverage.OVERLAP_CHANGED')}
          </Link>,
        );
      }
      break;
    case 'CURRENT_EXPENSES_ONCE':
      if (d.duplicatePayrollExpenses?.length) lines.push(t('fr.rec.duplicatePayroll', { n: d.duplicatePayrollExpenses.length }));
      if (d.payrollCountedFromPayroll?.length) lines.push(t('fr.rec.payrollFromPayroll', { n: d.payrollCountedFromPayroll.length }));
      if (d.handSalariesInPayrollMonths?.length) lines.push(t('fr.rec.handSalaries', { n: d.handSalariesInPayrollMonths.reduce((s: number, h: any) => s + h.count, 0) }));
      break;
    case 'NO_FINANCING_IN_OPERATIONS':
      lines.push(t('fr.rec.byConstruction'));
      break;
    case 'HISTORICAL_NO_CASH':
      lines.push(t('fr.rec.byConstruction'));
      if (d.reachingCutover?.length) lines.push(t('fr.rec.reachingCutover', { n: d.reachingCutover.length }));
      break;
    case 'CURRENCY_CONSISTENCY':
      if (d.unconverted?.length) lines.push(t('fr.rec.unconverted', { currencies: d.unconverted.join(', ') }));
      if (d.atTodayRate?.length) lines.push(t('fr.rec.todayRate', { currencies: d.atTodayRate.join(', ') }));
      if (d.valuedInOlderBase?.length) lines.push(t('fr.rec.olderBase', { n: d.valuedInOlderBase.reduce((s: number, r: any) => s + r.count, 0) }));
      lines.push(t('fr.notice.PER_CURRENCY'));
      break;
    case 'DOCUMENT_COVERAGE':
      if (d.notProvided?.length) lines.push(t('fr.rec.notProvided', { months: months(d.notProvided) }));
      if (d.missingFiles?.length) lines.push(t('fr.rec.missingFiles', { months: months(d.missingFiles) }));
      if (d.drafts?.length) lines.push(t('fr.rec.drafts', { months: months(d.drafts) }));
      lines.push(t('fr.rec.corrections', { corrections: d.corrections ?? 0, voided: d.voided ?? 0 }));
      break;
  }
  if (check.status === 'RESTRICTED') lines.push(t('fr.statusHint.RESTRICTED'));
  if (check.status === 'UNAVAILABLE') lines.push(t(d.reason === 'UNAVAILABLE' ? 'fr.statusHint.UNAVAILABLE' : 'fr.statusHint.SETUP_NEEDED'));
  if (lines.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1 text-body-sm text-[var(--text-secondary)]">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/* Drilldown — the rows behind a figure (§5.3)                                 */
/* -------------------------------------------------------------------------- */

const PNL_LINES = ['CUSTOMER_PAYMENTS', 'REFUNDS', 'SALARY_EXPENSES', 'PAYROLL_WITHOUT_EXPENSE', 'POINTS_CASH', 'OTHER_EXPENSES', 'HISTORICAL_SALARIES', 'HISTORICAL_EXPENSES'];

/** Where each source row opens, in Finance. */
function rowHref(row: any): string | null {
  switch (row.source) {
    case 'Payment':
      return row.parentId ? `/invoices/${row.parentId}` : null;
    case 'Expense':
      return row.parentId ? `/payroll/${row.parentId}` : '/expenses';
    case 'PayrollPeriod':
      return `/payroll/${row.id}`;
    case 'WalletCashReward':
      return '/points-rewards';
    case 'HistoricalFinancialRecord':
      return `/historical/${row.id}`;
    case 'PartnerTransaction':
      return `/partner-transactions/${row.id}`;
    default:
      return null;
  }
}

export function ReportDrilldownPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [page, setPage] = useState(1);
  const line = params.get('line') ?? '';
  const query = {
    line,
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    categoryId: params.get('categoryId') ?? undefined,
    currency: params.get('currency') ?? undefined,
    page: String(page),
    pageSize: '50',
  };
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/drilldown', query, !!line);
  // A P&L line reads as the income statement names it; a cash line, as the cash flow does.
  const label = (key: string) => t((PNL_LINES.includes(key) ? `fr.drill.line.${key}` : `fr.cf.line.${key}`) as TranslationKey);
  const base: string = data?.context?.baseCurrency ?? 'EGP';

  return (
    <>
      <PageHeader
        title={t('fr.drill.title')}
        subtitle={line ? `${label(line)} · ${query.from ? formatDate(query.from, locale) : ''} – ${query.to ? formatDate(query.to, locale) : ''}` : undefined}
        action={
          <Button size="sm" icon={<ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />} onClick={() => navigate(-1)}>
            {t('fr.drill.back')}
          </Button>
        }
      />
      {!line ? (
        <EmptyState />
      ) : isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-4">
          <ContextBar context={data.context} hideNotices />
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <p className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('fr.drill.lineTotal')}</p>
              <ul className="mt-2 space-y-0.5">
                {data.lineTotal.length === 0 ? <li className="text-title">—</li> : data.lineTotal.map((c: any) => <li key={c.currency} className="text-title tabular">{formatMoney(c.amount, c.currency, locale)}</li>)}
              </ul>
              {data.figure && data.figure.byCurrency.some((c: any) => c.currency !== base) && (
                <p className="mt-1 text-caption text-muted">
                  {t('fr.drill.inBase', { currency: base })}: <FigureValue figure={data.figure} currency={base} />
                </p>
              )}
            </Card>
            <Card>
              <p className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('fr.drill.rowsTotal')}</p>
              <ul className="mt-2 space-y-0.5">
                {data.rowsTotal.length === 0 ? <li className="text-title">—</li> : data.rowsTotal.map((c: any) => <li key={c.currency} className="text-title tabular">{formatMoney(c.amount, c.currency, locale)}</li>)}
              </ul>
              {data.hiddenSources.length === 0 && JSON.stringify(data.rowsTotal.map((c: any) => [c.currency, c.amount])) === JSON.stringify(data.lineTotal.map((c: any) => [c.currency, c.amount])) && (
                <p className="mt-1 text-caption text-success-fg dark:text-success">{t('fr.drill.agrees')}</p>
              )}
            </Card>
          </div>
          {data.hiddenSources.length > 0 && (
            <p className="flex items-center gap-2 text-body-sm text-warning-fg dark:text-warning-dark">
              <Lock className="h-4 w-4" aria-hidden />
              {t('fr.drill.hidden', { sources: data.hiddenSources.map((s: string) => t(`fr.drill.source.${s}` as TranslationKey)).join(', ') })}
            </p>
          )}
          <Card padded={false}>
            {data.rows.length === 0 ? (
              <EmptyState />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('fr.drill.date')}</Th>
                    <Th>{t('fr.drill.source')}</Th>
                    <Th>{t('fr.drill.reference')}</Th>
                    <Th>{t('fr.drill.description')}</Th>
                    <Th align="end">{t('fr.amount')}</Th>
                    <Th align="end">{t('fr.drill.inBase', { currency: base })}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row: any) => {
                    const href = rowHref(row);
                    return (
                      <Tr key={`${row.source}-${row.id}`} onClick={href ? () => navigate(href) : undefined}>
                        <Td className="whitespace-nowrap tabular text-muted">{formatDate(row.day, locale)}</Td>
                        <Td>
                          <Badge tone="neutral">{t(`fr.drill.source.${row.source}` as TranslationKey)}</Badge>
                        </Td>
                        <Td className="font-mono text-mono-sm">{row.reference ?? '—'}</Td>
                        <Td className="max-w-[28rem] truncate">{row.description ?? '—'}</Td>
                        <Td align="end" className="tabular">{formatMoney(row.amount, row.currency, locale)}</Td>
                        <Td align="end" className="tabular text-muted">
                          {row.base === null ? '—' : formatMoney(row.base, base, locale)}
                          {row.valuation === 'TODAY_RATE' && <span className="ms-1" title={t('fr.atTodayRate')}>≈</span>}
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            <Pagination page={data.page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} onChange={setPage} />
          </Card>
        </div>
      )}
    </>
  );
}
